import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, User } from "lucide-react";
import { buildClientSnapshot } from "./clientSnapshotBuilder";

/**
 * Collapsible Client Snapshot panel for ItineraryView.
 * Styled to match Filters row and Itinerary View (rounded card, blue accents).
 * Default: collapsed. Shows 2–3 high-signal inline items when collapsed; chips + grouped bullets when expanded.
 */
export default function ClientSnapshot({
  questionnaireData,
  defaultCollapsed = true,
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const snapshot = useMemo(
    () => buildClientSnapshot(questionnaireData || {}),
    [questionnaireData]
  );

  const { chips, sections, flags } = snapshot;
  const hasContent = chips.length > 0 || sections.length > 0 || flags.length > 0;

  // High-signal inline subtext when collapsed (e.g. "Hopper • Allergies • Kids")
  const collapsedSubtext = useMemo(() => {
    const parts = [];
    if (chips.some((c) => c.key === "hopper")) parts.push("Hopper");
    if (chips.some((c) => c.key === "allergies")) parts.push("Allergies");
    if (chips.some((c) => c.key === "kids")) parts.push("Kids");
    if (parts.length === 0 && chips.length) {
      const first = chips.slice(0, 3).map((c) => c.label);
      return first.join(" • ");
    }
    return parts.join(" • ");
  }, [chips]);

  if (!hasContent) return null;

  return (
    <div className="px-6 py-3 w-full max-w-7xl mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Header — matches Filters row: icon + title + subtext + toggle */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex gap-3 items-center justify-between">
            <div className="flex gap-3 items-center min-w-0 flex-1">
              <div className="flex shrink-0 justify-center items-center w-8 h-8 rounded-lg bg-blue-50 text-blue-600">
                <User className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-gray-900">Client Snapshot</h2>
                {collapsed && collapsedSubtext && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">{collapsedSubtext}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className={`flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                !collapsed
                  ? "text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100"
                  : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
              }`}
              aria-expanded={!collapsed}
            >
              {collapsed ? "Show" : "Hide"}
              {collapsed ? (
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
              ) : (
                <ChevronUp className="w-3.5 h-3.5 text-blue-600" />
              )}
            </button>
          </div>
        </div>

        {!collapsed && (
          <div className="px-4 py-4 space-y-4 bg-gray-50/50">
            {flags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {flags.map((f) => (
                  <span
                    key={f}
                    className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-amber-800 bg-amber-50 rounded-md border border-amber-200"
                  >
                    {f}
                  </span>
                ))}
              </div>
            )}
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {chips.map((c) => (
                  <span
                    key={c.key}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white rounded-md border border-gray-200 shadow-sm"
                  >
                    {c.label}
                  </span>
                ))}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sections.map((sec) => (
                <div
                  key={sec.title}
                  className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                >
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    {sec.title}
                  </h3>
                  <ul className="list-none space-y-1.5 text-sm text-gray-700">
                    {sec.bullets.map((b, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-blue-500 shrink-0">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
